import React, { useState } from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';

interface BusinessHoursCollapsibleProps {
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    scheduleData: {
        days: {
            [key: string]: { holiday: boolean; time: string };
        };
    };
    onScheduleChange: (day: string, field: 'holiday' | 'time', value: boolean | string) => void;
    title?: string;
    fieldPrefix?: string;
}

const BusinessHoursCollapsible: React.FC<BusinessHoursCollapsibleProps> = ({
    register, errors, scheduleData, onScheduleChange, title = "Active Hours", fieldPrefix = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const days = [
        { key: 'monday', label: 'Monday' },
        { key: 'tuesday', label: 'Tuesday' },
        { key: 'wednesday', label: 'Wednesday' },
        { key: 'thursday', label: 'Thursday' },
        { key: 'friday', label: 'Friday' },
        { key: 'saturday', label: 'Saturday' },
        { key: 'sunday', label: 'Sunday' }
    ];

    const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>, day: string) => {
        const isHoliday = e.target.checked;
        onScheduleChange(day, 'holiday', isHoliday);
        if (isHoliday) onScheduleChange(day, 'time', '');
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>, day: string) => {
        const time = e.target.value.replace(/am/g, "AM").replace(/pm/g, "PM").trim();
        onScheduleChange(day, 'time', time);
    };

    // Check if there are any validation errors for this section
    const hasValidationErrors = days.some(({ key }) => {
        const isHoliday = scheduleData.days[key]?.holiday || false;
        return errors[`${fieldPrefix}${key}Text`]?.type === "required" && !isHoliday;
    });

    return (
        <div className="row mb-2 mt-2">
            <div className="col-md-4">
                <label><b>{title}</b></label>
            </div>
            <div className="col-md-4">
                {/* Collapsible Header */}
                <div
                    className={`d-flex align-items-center justify-content-between p-2 bg-light border rounded cursor-pointer ${hasValidationErrors && !isOpen ? 'border-danger' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                    style={{ cursor: 'pointer' }}
                >
                    <span className={hasValidationErrors && !isOpen ? 'text-danger' : 'text-primary'}>
                        Configure Schedule
                        {hasValidationErrors && !isOpen && <span className="ms-2">⚠️</span>}
                    </span>
                    <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} ${hasValidationErrors && !isOpen ? 'text-danger' : 'text-primary'}`}></i>
                </div>

                {/* Show validation error message when collapsed */}
                {hasValidationErrors && !isOpen && (
                    <small className="text-danger d-block mt-1">
                        Please configure business hours for all days
                    </small>
                )}

                {/* Collapsible Content */}
                {isOpen && (
                    <div className="border border-top-0 rounded-bottom p-3" style={{ fontSize: '0.95rem' }}>
                        <div className="row mb-2">
                            <div className="col-md-4"><strong>Day</strong></div>
                            <div className="col-md-2"><strong>Holiday</strong></div>
                            <div className="col-md-6"><strong>Business Hours</strong></div>
                        </div>

                        {days.map(({ key, label }) => {
                            const isHoliday = scheduleData.days[key]?.holiday || false;
                            const hasError = errors[`${fieldPrefix}${key}Text`]?.type === "required" && !isHoliday;

                            return (
                                <div className='row mb-2' key={key}>
                                    <div className="col-md-4">
                                        <label className="fw-semibold">{label}</label>
                                    </div>
                                    <div className="col-md-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            {...register(`${fieldPrefix}${key}`, {
                                                onChange: (e) => handleDayChange(e, key)
                                            })}
                                            checked={isHoliday}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        {isHoliday ? (
                                            <span className="text-muted">Closed</span>
                                        ) : (
                                            <>
                                                <input
                                                    type="text"
                                                    className={`form-control form-control-sm ${hasError ? 'is-invalid' : ''}`}
                                                    placeholder="09:30AM-12:30PM,01:30PM-06:30PM"
                                                    value={scheduleData.days[key]?.time || ''}
                                                    {...register(`${fieldPrefix}${key}Text`, {
                                                        required: !isHoliday,
                                                        onChange: (e) => handleTimeChange(e, key)
                                                    })}
                                                />
                                                {hasError && (
                                                    <small className="text-danger">Please specify hours or mark as holiday</small>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Hidden fields to ensure validation works even when collapsed */}
            <div style={{ display: 'none' }}>
                {days.map(({ key }) => {
                    const isHoliday = scheduleData.days[key]?.holiday || false;
                    if (!isHoliday && !isOpen) {
                        return (
                            <input
                                key={`hidden-${fieldPrefix}${key}`}
                                type="text"
                                {...register(`${fieldPrefix}${key}Text`, {
                                    required: !isHoliday
                                })}
                                value={scheduleData.days[key]?.time || ''}
                            />
                        );
                    }
                    return null;
                })}
            </div>
        </div>
    );
};

export default BusinessHoursCollapsible;